import { Component, Input, Output, EventEmitter, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { combineLatest, Observable, of, Subject, throwError, forkJoin } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Deal } from 'ng2-app/models/dto/deal.model';
import { FormlyDialogComponent, FormlyDialogData } from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { DataListItem } from '../../../borrower-overview/data-list/data-list.component';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BorrowerValueService } from 'ng2-app/services/borrower-value/borrower-value.service';
import { UntypedFormGroup } from '@angular/forms';
import { DealApplicationStatusHelperService } from './../deal-application-status-helper.service';
import { QuestionsListStatus } from 'app/models/dto/question.model';
import { FormlyModel } from 'app/form-core/formly/formly-core.config';
import { UpdateAnswers } from 'app/store/deal/deal-application-requirements/deal-application-requirement.actions';
import { Store } from '@ngxs/store';
import { map, take } from 'rxjs/operators';
import { BorrowerValueState } from 'ng2-app/store/borrower-value/borrower-value.state';
import { ContactState } from 'ng2-app/store/contact/contact.state';
import { Contact } from 'app/models/dto/contact.model';
import {GetBorrowerValues, SaveBorrowerValues} from 'ng2-app/store/borrower-value/borrower-value.actions';
import { UpdateContact } from 'ng2-app/store/contact/contact.actions';

@Component({
  selector: 'app-deal-app-status-smart',
  templateUrl: './deal-app-status-smart.component.html',
  styleUrls: ['./deal-app-status-smart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DealAppStatusSmartComponent implements OnChanges {

  @Input() deal: Deal;
  @Input() borrowerId: number;
  @Input() loadingStatus = true;
  @Input() questionListStatus: QuestionsListStatus;
  @Input() advancedDeal = false;
  @Output() change = new EventEmitter();
  percentComplete = 0;
  showEditLink = true;
  destroyed$ = new Subject<boolean>();
  dataList$: Observable<DataListItem[]>;
  labels$: Observable<string[]>;
  formlyFields: FormlyFieldConfig[];
  formlyModel: FormlyModel;
  private originalModel: FormlyModel;
  private dialogRef: MatDialogRef<FormlyDialogComponent>;


  constructor(
    public dialog: MatDialog,
    private borrowerValueService: BorrowerValueService,
    private store: Store,
    private dealApplicationStatusHelperService: DealApplicationStatusHelperService
  ) { }

  ngOnChanges(): void {
    if (this.questionListStatus) {
      this.percentComplete = this.questionListStatus.percentComplete;
      this.showEditLink = !(this.questionListStatus.fulfilledCount === 0 && this.questionListStatus.requiredCount === 0);
    }
  }

  openQuestionDialog(): void {
    const grouped = this.dealApplicationStatusHelperService.getGroupedFormlyFields(this.questionListStatus.application);
    const borrowerValues = grouped.filter(item => item.type !== 'document');

    this.getPhoneAndMobileCodes()
      .pipe(take(1))
      .subscribe(([phoneCode, mobileCode]) => {
        const allValues = [ ...borrowerValues, phoneCode, mobileCode ].filter(Boolean as any);
        this.buildAndOpenDialog(allValues);
      });
  }

  private buildAndOpenDialog(values: any[]): void {
    const hyphenValues = this.dealApplicationStatusHelperService.convertKeyValuestoHypenFormat(values);

    this.formlyModel = this.borrowerValueService.borrowerValuesToFormlyModel(hyphenValues);
    this.originalModel = structuredClone(this.formlyModel);
    const fieldConfigs = this.borrowerValueService.borrowerValuesToFormlyFieldConfigs(this.formlyModel, hyphenValues);
    this.formlyFields = this.dealApplicationStatusHelperService.modifyFormlyFields(fieldConfigs, this.questionListStatus.application);

    this.annotateRequiredFields();

    this.dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: this.getDialogData(),
      width: '50rem',
      panelClass: 'question-dialog-container',
      maxHeight: '100vh',
    });

    this.dialogRef.componentInstance.saveData.subscribe(result => this.onSave(result));
    this.dialogRef.afterClosed().subscribe(result => result && this.onSave(result));
  }

  private annotateRequiredFields(): void {
    this.formlyFields.forEach(field => {
      const hasValue = Boolean(this.formlyModel[field.key as string]);
      field.templateOptions = field.templateOptions || {};
      field.templateOptions.required = hasValue;
      field.className = hasValue
        ? (field.className || '').replace(/\brequired\b/, '').trim()
        : [field.className, 'required'].filter(Boolean).join(' ');
    });
  }

  private getPhoneAndMobileCodes(): Observable<[any|null, any|null]> {
    return combineLatest([
      this.store.select(BorrowerValueState.valueByAlias).pipe(map(fn => fn('borrowerPhoneCountryCodeId'))),
      this.store.select(ContactState.primaryContact).pipe(
        map(contact => contact?.mobilePhoneCountryCodeId
          ? { alias: 'mobilePhoneCountryCodeId', value: contact.mobilePhoneCountryCodeId }
          : null
        )
      )
    ]);
  }

  private getDialogData(): FormlyDialogData {
    return {
      loading$: of(false),
      title: `${this.deal.loan_product_lender.name} ${this.deal.loan_product.name}`,
      errorMessage: '',
      formlyConfig: {
        model: this.formlyModel,
        form: new UntypedFormGroup({}),
        fields: this.dealApplicationStatusHelperService.getModifiedFieldswithCSSApplied(this.formlyFields),
        options: {},
      }
    };
  }

  private onSave(result: { formlyConfig: { model: FormlyModel } }): void {
    if (this.dialogRef?.componentInstance) {
      this.dialogRef.componentInstance.data.errorMessage = '';
      this.dialogRef.componentInstance.data.loading$ = of(true);
    }

    const changes = this.dealApplicationStatusHelperService.getDiffProperties(
      this.formlyFields,
      this.originalModel,
      this.formlyModel
    );

    this.store.dispatch(new UpdateAnswers(this.deal, changes))
      .pipe(take(1))
      .subscribe({
        next: () => this.handleSaveSuccess(result, changes),
        error: (err) => this.handleSaveError(err),
      });
  }

  private handleSaveSuccess(result: any, changes: any[]): void {
    this.dialogRef?.close();
    this.formlyModel = result.formlyConfig?.model;

    changes.forEach(change => {
      if (change.alias === 'contact.mobilePhone') {
        this.updateContact(change.value);
      }
      if (change.alias === 'borrower.phone') {
        this.updateBorrower(change.value);
      }
    });
  }

  private handleSaveError(error: any): void {
    if (this.dialogRef?.componentInstance) {
      this.dialogRef.componentInstance.data.loading$ = of(false);
      this.dialogRef.componentInstance.data.errorMessage = error.message || 'Failed to update borrower info';
    }
  }

  private updateContact(value: { phone: string, countryCode: number }): void {
    this.store.select(ContactState.primaryContact)
      .pipe(take(1))
      .subscribe(contact => {
        if (!contact) {
          return;
        }

        contact.mobilePhone = value.phone;
        contact.mobilePhoneCountryCodeId = value.countryCode;
        this.store.dispatch(new UpdateContact(contact as Contact));
      });
  }

  private updateBorrower(value: { phone: string, countryCode: number }): void {
    forkJoin({
      borrowerPhoneValue: this.store.select(BorrowerValueState.valueByAlias)
        .pipe(map(fn => fn('borrowerPhone')), take(1)),
      borrowerPhoneCountryCodeIdValue: this.store.select(BorrowerValueState.valueByAlias)
        .pipe(map(fn => fn('borrowerPhoneCountryCodeId')), take(1))
    }).subscribe(({ borrowerPhoneValue, borrowerPhoneCountryCodeIdValue }) => {
        const updates: any[] = [];
        if (borrowerPhoneValue) {
          borrowerPhoneValue.value = value.phone;
          updates.push(borrowerPhoneValue);
        }
        if (borrowerPhoneCountryCodeIdValue) {
          // @ts-ignore
          borrowerPhoneCountryCodeIdValue.value = value.countryCode;
          updates.push(borrowerPhoneCountryCodeIdValue);
        }

        if (updates.length) {
          this.store.dispatch(new SaveBorrowerValues(this.borrowerId, updates));
        }
      });
  }
}
